import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Search, ChevronDown, ChevronRight, Printer, RefreshCw } from 'lucide-react';
import OrderStatusTracker from '../../components/OrderStatusTracker';
import ConfirmModal from '../../components/ConfirmModal';

export default function ManageOrders() {
    const toast = useToast();
    const [orders, setOrders] = useState([]);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deliveryConfig, setDeliveryConfig] = useState(null);
    const [adloggsLoading, setAdloggsLoading] = useState(null);
    const [deliveryMode, setDeliveryMode] = useState('own');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedRow, setExpandedRow] = useState(null);
    const [selectedTab, setSelectedTab] = useState('all'); // 'all', 'online', 'dine_in', 'takeaway'
    const itemsPerPage = 10;
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [trackingDetails, setTrackingDetails] = useState(null);
    const [fetchingTracking, setFetchingTracking] = useState(false);
    const [showRawResponse, setShowRawResponse] = useState(false);

    const [cancelConfirm, setCancelConfirm] = useState(null);
    const [cancelReason, setCancelReason] = useState('');

    const handleAdminCancel = async () => {
        setActionLoading(cancelConfirm);
        try {
            await api.post(`/api/orders/${cancelConfirm}/cancel`, { reason: 'Cancelled by admin' });
            toast.success('Order cancelled successfully');
            await fetchData();
            setCancelConfirm(null);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to cancel order');
        } finally {
            setActionLoading(null);
        }
    };

    const handleTrackDelivery = async (orderId) => {
        setTrackingOrder(orderId);
        setTrackingDetails(null);
        setFetchingTracking(true);
        try {
            const res = await api.get(`/api/external-delivery/track/${orderId}`);
            setTrackingDetails(res.data?.data || res.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to fetch live tracking details');
        } finally {
            setFetchingTracking(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedTab, statusFilter]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const ordersRes = await api.get('/api/orders/all');
            setOrders(ordersRes.data || []);
        } catch (error) {
            console.error('Fetch orders error:', error);
            toast.error('Failed to load orders');
        }

        try {
            const partnersRes = await api.get('/api/delivery-partner/available');
            setPartners(partnersRes.data || []);
        } catch (error) {
            console.error('Fetch partners error:', error);
        }
        
        try {
            const configRes = await api.get('/api/external-delivery/config');
            const configs = configRes.data || [];
            const adloggsConf = configs.find(c => c.provider === 'adloggs' && c.is_active);
            setDeliveryConfig({ hasAdloggs: !!adloggsConf });
        } catch (err) {
            setDeliveryConfig({ hasAdloggs: false });
        }

        try {
            const opRes = await api.get('/api/restaurants/settings/operational');
            setDeliveryMode(opRes.data.delivery_mode || 'own');
        } catch (err) {
            setDeliveryMode('own');
        } finally {
            setLoading(false);
        }
    };

    const handleAssignAdloggs = async (orderId) => {
        setAdloggsLoading(orderId);
        try {
            await api.post('/api/external-delivery/assign', { orderId, provider: 'adloggs' });
            toast.success('Assigned to Adloggs successfully!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to assign to Adloggs');
        } finally {
            setAdloggsLoading(null);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchData();
            toast.success('Orders refreshed successfully');
        } catch (error) {
            toast.error('Failed to refresh orders');
        } finally {
            setIsRefreshing(false);
        }
    };

    const printOrderBill = (order) => {
        const printWindow = window.open('', '_blank');
        
        const subtotal = order.items.reduce((sum, item) => sum + (parseFloat(item.unit_price) * item.quantity), 0);
        const gstAmount = parseFloat(order.tax_amount || 0);
        const totalAmount = parseFloat(order.final_total_amount || order.total_amount || 0);

        const itemsHtml = order.items.map(item => `
            <tr>
                <td style="padding: 4px 0;">${item.name} x ${item.quantity}</td>
                <td style="padding: 4px 0; text-align: right;">₹${(parseFloat(item.unit_price) * item.quantity).toFixed(2)}</td>
            </tr>
        `).join('');

        const orderType = order.delivery_address === 'Dine-In' ? 'DINE-IN' : 
                          order.delivery_address === 'Store Takeaway' ? 'TAKEAWAY' : 'ONLINE DELIVERY';

        printWindow.document.write(`
            <html>
            <head>
                <title>order_${order.id}</title>
                <style>
                    @page { margin: 0; }
                    body {
                        font-family: 'Courier New', Courier, monospace;
                        width: 76mm;
                        margin: 0;
                        padding: 10px;
                        font-size: 12px;
                        color: #000;
                        line-height: 1.4;
                    }
                    .text-center { text-align: center; }
                    .dashed-line { border-top: 1px dashed #000; margin: 8px 0; }
                    .bold { font-weight: bold; }
                    table { width: 100%; border-collapse: collapse; }
                    th, td { text-align: left; }
                </style>
            </head>
            <body>
                <div class="text-center">
                    <span style="font-size: 16px; font-weight: bold;">Savory Bites</span><br/>
                    <span>POS BILLING RECEIPT</span><br/>
                    <span>Anna Nagar, Chennai</span>
                </div>
                <div class="dashed-line"></div>
                <div>
                    <span><strong>Bill ID:</strong> POS-${order.id}</span><br/>
                    <span><strong>Date:</strong> ${new Date(order.created_at).toLocaleString()}</span><br/>
                    <span><strong>Type:</strong> ${orderType}</span><br/>
                    ${order.customer_name && order.customer_name !== 'Walk-in Customer' ? `<span><strong>Cust:</strong> ${order.customer_name}</span><br/>` : ''}
                    ${order.contact_phone && order.contact_phone !== 'N/A' ? `<span><strong>Phone:</strong> ${order.contact_phone}</span><br/>` : ''}
                </div>
                <div class="dashed-line"></div>
                <table>
                    <thead>
                        <tr class="bold">
                            <th style="padding-bottom: 5px;">Item</th>
                            <th style="padding-bottom: 5px; text-align: right;">Amt</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsHtml}
                    </tbody>
                </table>
                <div class="dashed-line"></div>
                <table class="bold">
                    <tr>
                        <td>Subtotal</td>
                        <td style="text-align: right;">₹${subtotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td>CGST/SGST (5%)</td>
                        <td style="text-align: right;">₹${gstAmount.toFixed(2)}</td>
                    </tr>
                    ${order.delivery_fee > 0 ? `
                    <tr>
                        <td>Delivery Charge</td>
                        <td style="text-align: right;">₹${parseFloat(order.delivery_fee).toFixed(2)}</td>
                    </tr>
                    ` : ''}
                    <tr style="font-size: 14px;">
                        <td>GRAND TOTAL</td>
                        <td style="text-align: right;">₹${totalAmount.toFixed(2)}</td>
                    </tr>
                </table>
                <div class="dashed-line"></div>
                <div class="text-center">
                    <span>Status: <span style="text-transform: uppercase;">${order.payment_status || 'PAID'}</span></span><br/>
                    <span class="bold">THANK YOU! VISIT AGAIN</span><br/>
                    <span style="font-size: 8px;">Aaharam Cloud POS Terminal v1.0</span>
                </div>
                <script>
                    window.onload = function() {
                        document.title = "order_${order.id}";
                        window.print();
                        setTimeout(function() { window.close(); }, 500);
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.title = `order_${order.id}`;
        printWindow.document.close();
    };

    const handleStatusChange = async (orderId, newStatus) => {
        if (newStatus === 'cancelled') {
            setCancelConfirm(orderId);
            return;
        }
        setActionLoading(orderId);
        try {
            await api.patch(`/api/orders/${orderId}/admin-status`, { status: newStatus });
            await fetchData();
        } catch (error) {
            toast.error('Failed to update status');
        } finally {
            setActionLoading(null);
        }
    };

    const handleAssignPartner = async (orderId, partnerId) => {
        if (!partnerId) return;
        setActionLoading(orderId);
        try {
            await api.post('/api/delivery-partner/assign', { orderId, partnerId });
            toast.success('Partner assigned successfully! OTP will be sent to the customer when the partner accepts the order.');
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to assign partner');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
            <div className="animate-spin w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full"></div>
            <p className="text-sm font-semibold text-gray-500">Loading orders...</p>
        </div>
    );

    const statuses = ['pending', 'confirmed', 'preparing', 'ready', 'delivered', 'cancelled'];

    const filteredOrders = orders.filter(o => {
        // Section Filter
        if (selectedTab === 'dine_in' && o.delivery_address !== 'Dine-In') return false;
        if (selectedTab === 'takeaway' && o.delivery_address !== 'Store Takeaway') return false;
        if (selectedTab === 'online' && (o.delivery_address === 'Dine-In' || o.delivery_address === 'Store Takeaway')) return false;

        // Status Filter
        if (statusFilter !== 'all' && o.status !== statusFilter) return false;

        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (String(o.id).includes(s) || o.customer_name?.toLowerCase().includes(s) || o.contact_phone?.includes(s));
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

    return (
        <div className="space-y-6 text-left">
            {actionLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex flex-col items-center justify-center backdrop-blur-sm">
                    <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-sm w-full mx-4 transform transition-all">
                        <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mb-4"></div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Processing Request</h3>
                        <p className="text-sm text-gray-500 text-center">Please wait while we update the system...</p>
                    </div>
                </div>
            )}
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Manage Orders</h1>
                        <p className="text-xs text-gray-400 mt-0.5">Track and update delivery schedules, kitchen outputs, and POS walk-in status.</p>
                    </div>
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="p-2 text-gray-600 hover:text-primary-600 bg-white border border-gray-200 rounded-xl hover:border-primary-300 shadow-sm transition-all flex items-center justify-center disabled:opacity-50"
                        title="Refresh Orders"
                    >
                        <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-primary-600' : ''}`} />
                    </button>
                </div>

                {/* Section Filter Tabs */}
                <div className="flex bg-gray-100 p-1 rounded-xl w-fit self-start md:self-auto">
                    <button
                        onClick={() => { setSelectedTab('all'); setCurrentPage(1); }}
                        className={`py-1.5 px-4 text-xs font-bold rounded-lg transition-all ${
                            selectedTab === 'all' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
                        }`}
                    >
                        All ({orders.length})
                    </button>
                    <button
                        onClick={() => { setSelectedTab('online'); setCurrentPage(1); }}
                        className={`py-1.5 px-4 text-xs font-bold rounded-lg transition-all ${
                            selectedTab === 'online' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
                        }`}
                    >
                        Online ({orders.filter(o => o.delivery_address !== 'Dine-In' && o.delivery_address !== 'Store Takeaway').length})
                    </button>
                    <button
                        onClick={() => { setSelectedTab('dine_in'); setCurrentPage(1); }}
                        className={`py-1.5 px-4 text-xs font-bold rounded-lg transition-all ${
                            selectedTab === 'dine_in' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
                        }`}
                    >
                        Dine-In ({orders.filter(o => o.delivery_address === 'Dine-In').length})
                    </button>
                    <button
                        onClick={() => { setSelectedTab('takeaway'); setCurrentPage(1); }}
                        className={`py-1.5 px-4 text-xs font-bold rounded-lg transition-all ${
                            selectedTab === 'takeaway' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
                        }`}
                    >
                        Takeaway ({orders.filter(o => o.delivery_address === 'Store Takeaway').length})
                    </button>
                </div>
            </div>

            {/* Search and Status Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by order ID, customer name, or phone..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-white outline-none text-sm font-bold text-gray-700 sm:w-48 cursor-pointer"
                >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="preparing">Preparing</option>
                    <option value="ready">Ready</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery Assignment</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentOrders.map(order => (
                            <React.Fragment key={order.id}>
                                <tr 
                                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${expandedRow === order.id ? 'bg-primary-50/20' : ''}`}
                                    onClick={() => setExpandedRow(expandedRow === order.id ? null : order.id)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center gap-2">
                                        {expandedRow === order.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                        <div className="flex flex-col text-left">
                                            <span className="font-bold">#{order.id}</span>
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold w-fit mt-1 ${
                                                order.delivery_address === 'Dine-In'
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : order.delivery_address === 'Store Takeaway'
                                                    ? 'bg-purple-100 text-purple-700'
                                                    : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                                {order.delivery_address === 'Dine-In' ? 'Dine-In' : order.delivery_address === 'Store Takeaway' ? 'Takeaway' : 'Online'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">{order.customer_name}</div>
                                        <div className="text-gray-500 text-xs">{order.contact_phone}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">
                                        ₹{parseFloat(order.final_total_amount || order.total_amount || 0).toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <select
                                            value={order.status}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                            disabled={actionLoading === order.id || order.status === 'delivered' || order.status === 'cancelled'}
                                            className={`font-semibold border rounded-lg px-2.5 py-1.5 text-xs uppercase tracking-wider outline-none cursor-pointer disabled:opacity-50 transition-all ${
                                                order.status === 'delivered' ? 'bg-green-50 text-green-700 border-green-200' :
                                                order.status === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
                                                order.status === 'preparing' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                order.status === 'ready' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                                order.status === 'confirmed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                'bg-gray-50 text-gray-700 border-gray-200'
                                            }`}
                                        >
                                            {statuses.map(s => (
                                                <option key={s} value={s} className="bg-white text-gray-800 font-semibold uppercase">
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {order.delivery_address === 'Dine-In' ? (
                                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-bold uppercase tracking-wider">
                                                Table Service
                                            </span>
                                        ) : order.delivery_address === 'Store Takeaway' ? (
                                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-lg text-xs font-bold uppercase tracking-wider">
                                                Self Takeaway
                                            </span>
                                        ) : order.delivery_type === 'external' ? (
                                            <div className="flex flex-col text-left">
                                                <span className={`font-medium px-2 py-1 rounded capitalize w-fit mb-1 ${
                                                    order.delivery_status === 'delivered' ? 'text-green-700 bg-green-50' :
                                                    order.delivery_status === 'picked_up' || order.delivery_status === 'in_transit' ? 'text-blue-700 bg-blue-50' :
                                                    'text-purple-700 bg-purple-50'
                                                }`}>
                                                    {order.delivery_status ? order.delivery_status.replace(/_/g, ' ') : 'Assigned'}
                                                </span>
                                                <div className="text-xs text-indigo-600 font-bold flex items-center gap-1">
                                                     🏍️ {order.external_provider === 'adloggs' ? 'Adloggs Delivery' : order.external_provider}
                                                </div>
                                                {order.external_courier_name && (
                                                    <div className="text-[10px] text-gray-500">Courier: {order.external_courier_name}</div>
                                                )}
                                                {order.external_awb_code && (
                                                    <div className="text-[10px] text-gray-400">CRN/ID: {order.external_awb_code}</div>
                                                )}
                                                <div className="flex flex-col gap-1 mt-1">
                                                    {order.external_tracking_url && (
                                                        <a
                                                            href={order.external_tracking_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-[10px] text-indigo-600 hover:underline font-bold text-left w-fit"
                                                        >
                                                            Track Delivery ↗
                                                        </a>
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleTrackDelivery(order.id);
                                                        }}
                                                        className="text-[10px] text-gray-600 hover:underline text-left w-fit font-medium flex items-center gap-0.5"
                                                    >
                                                        Check Live Status 🔄
                                                    </button>
                                                </div>
                                            </div>
                                        ) : order.delivery_partner_id ? (
                                            <div className="flex flex-col text-left">
                                                <span className={`font-medium px-2 py-1 rounded capitalize w-fit mb-1 ${order.delivery_status === 'delivered' ? 'text-green-700 bg-green-50' :
                                                        order.delivery_status === 'picked_up' ? 'text-blue-700 bg-blue-50' :
                                                            'text-indigo-700 bg-indigo-50'}`}>
                                                    {order.delivery_status ? order.delivery_status.replace('_', ' ') : 'Assigned'}
                                                </span>
                                                <div className="text-xs text-gray-600 font-medium">{order.partner_name}</div>
                                                <div className="text-[10px] text-gray-400">{order.partner_phone}</div>
                                            </div>
                                        ) : order.payment_status === 'paid' && (order.status === 'ready' || order.status === 'delivered') ? (
                                            <div className="flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                {/* Adloggs Delivery Option */}
                                                {(deliveryMode === 'partner' || deliveryMode === 'both') && deliveryConfig?.hasAdloggs && (
                                                    <button
                                                        onClick={() => handleAssignAdloggs(order.id)}
                                                        disabled={adloggsLoading === order.id || actionLoading === order.id}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-300 rounded-lg text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition-all disabled:opacity-50"
                                                    >
                                                        {adloggsLoading === order.id ? (
                                                            <><div className="animate-spin w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full"></div> Assigning...</>
                                                        ) : (
                                                            <><span>🏍️</span> Adloggs Delivery</>
                                                        )}
                                                    </button>
                                                )}
                                                {/* Own Delivery Partners */}
                                                {(deliveryMode === 'own' || deliveryMode === 'both') && (
                                                    <select
                                                        onChange={(e) => handleAssignPartner(order.id, e.target.value)}
                                                        defaultValue=""
                                                        disabled={actionLoading === order.id}
                                                        className="border border-blue-300 rounded px-2 py-1.5 text-sm bg-blue-50 text-blue-800 disabled:opacity-50 w-full"
                                                    >
                                                        <option value="" disabled>Assign Partner</option>
                                                        {partners.map(p => (
                                                            <option key={p.user_id} value={p.user_id}>{p.full_name} ({p.vehicle_type})</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 italic">
                                                {order.status !== 'ready' ? `Order is ${order.status}` : 'Not eligible'}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                {/* Detail Row for Tracker */}
                                {expandedRow === order.id && (
                                    <tr className="bg-gray-50/50">
                                        <td colSpan="5" className="px-6 py-8">
                                            <div className="max-w-3xl mx-auto bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                                <div className="flex justify-between items-center mb-6">
                                                    <h4 className="font-bold text-gray-900">Live Order Tracking</h4>
                                                    <div className="flex items-center space-x-3">
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); printOrderBill(order); }}
                                                            className="flex items-center space-x-1 px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-xl text-xs font-bold transition-all border border-primary-200"
                                                        >
                                                            <Printer className="w-3.5 h-3.5" />
                                                            <span>Print Bill</span>
                                                        </button>
                                                        <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2.5 py-1 rounded-lg">Order ID: #{order.id}</span>
                                                    </div>
                                                </div>
                                                <OrderStatusTracker order={order} />
                                                
                                                <div className="mt-8 grid grid-cols-2 gap-6 text-sm">
                                                    <div className="space-y-2">
                                                        <p className="text-gray-500 font-medium uppercase tracking-wider text-[10px]">Customer Details</p>
                                                        <p className="font-semibold text-gray-900">{order.customer_name}</p>
                                                        <p className="text-gray-600">{order.contact_phone}</p>
                                                        <p className="text-gray-600 line-clamp-2">{order.delivery_address}</p>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <p className="text-gray-500 font-medium uppercase tracking-wider text-[10px]">Order Summary</p>
                                                        <div className="max-h-32 overflow-y-auto space-y-1 pr-2">
                                                            {order.items.map((item, i) => (
                                                                <div key={i} className="flex justify-between text-gray-700">
                                                                    <span>{item.name} × {item.quantity}</span>
                                                                    <span className="font-medium">₹{parseFloat(item.unit_price * item.quantity).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {order.status === 'cancelled' && (
                                                    <div className="mt-6 bg-red-50 border border-red-100 rounded-xl p-4">
                                                        <h5 className="text-red-800 font-bold mb-2 flex items-center gap-2">
                                                            <span className="w-2 h-2 rounded-full bg-red-500"></span> Cancellation Details
                                                        </h5>
                                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                                            <div>
                                                                <span className="text-gray-500 block text-xs">Reason</span>
                                                                <span className="text-red-700 font-medium">{order.cancellation_reason || 'Not specified'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-gray-500 block text-xs">Cancelled At</span>
                                                                <span className="text-red-700 font-medium">
                                                                    {order.cancelled_at ? new Date(order.cancelled_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'N/A'}
                                                                </span>
                                                            </div>
                                                            {order.refund_status && order.refund_status !== 'none' && (
                                                                <div className="col-span-2 mt-2 pt-2 border-t border-red-100/50">
                                                                    <span className="text-gray-500 block text-xs mb-1">Refund Status</span>
                                                                    <span className={`px-2 py-1 rounded text-xs font-bold inline-block ${
                                                                        order.refund_status === 'initiated' ? 'bg-yellow-100 text-yellow-800' :
                                                                        order.refund_status === 'processed' ? 'bg-green-100 text-green-800' :
                                                                        'bg-red-100 text-red-800'
                                                                    }`}>
                                                                        {order.refund_status.toUpperCase()} 
                                                                        {order.refund_amount ? ` - ₹${parseFloat(order.refund_amount).toFixed(2)}` : ''}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50">
                        <div className="text-sm text-gray-500">
                            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredOrders.length)}</span> of <span className="font-medium">{filteredOrders.length}</span> results
                        </div>
                        <div className="flex space-x-2">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-50 hover:bg-gray-100"
                            >
                                Previous
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                                className="px-3 py-1 border rounded bg-white text-gray-600 disabled:opacity-50 hover:bg-gray-100"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Live Tracking Modal */}
            {trackingOrder && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative overflow-hidden text-left max-h-[90vh] flex flex-col">
                        <button
                            onClick={() => {
                                setTrackingOrder(null);
                                setTrackingDetails(null);
                                setShowRawResponse(false);
                            }}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 font-bold text-xl"
                        >
                            ✕
                        </button>
                        
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex-shrink-0">
                            Live Delivery Tracking
                        </h3>

                        <div className="overflow-y-auto flex-grow pr-1 space-y-4">
                            {fetchingTracking ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
                                    <p className="text-sm text-gray-500">Contacting Adloggs network for live update...</p>
                                </div>
                            ) : trackingDetails ? (
                                <>
                                    <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                                        <div className="text-xs text-indigo-500 font-semibold uppercase tracking-wider">Status</div>
                                        <div className="text-base font-bold text-indigo-900 mt-1 capitalize">
                                            {(() => {
                                                const statusId = trackingDetails.order_status_id;
                                                const desc = trackingDetails.order_status_description;
                                                if (desc) return desc;
                                                const map = {
                                                    2: 'Pending (Finding Rider)',
                                                    3: 'Assigned',
                                                    11: 'Arrived at Pickup',
                                                    4: 'Picked Up',
                                                    9: 'Out For Delivery',
                                                    8: 'Arrived',
                                                    5: 'Delivered',
                                                    6: 'Cancelled',
                                                    14: 'Cancelled'
                                                };
                                                return map[statusId] || `Status Code: ${statusId}`;
                                            })()}
                                        </div>
                                    </div>

                                    {trackingDetails.deliveryStaffDetails?.name && (
                                        <div className="border-t pt-3">
                                            <div className="text-xs text-gray-500">Rider Partner</div>
                                            <div className="font-bold text-gray-900 mt-0.5">{trackingDetails.deliveryStaffDetails.name}</div>
                                            {trackingDetails.deliveryStaffDetails.phone && (
                                                <a 
                                                    href={`tel:${trackingDetails.deliveryStaffDetails.phone}`}
                                                    className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1 font-medium"
                                                >
                                                    📞 Call: {trackingDetails.deliveryStaffDetails.phone}
                                                </a>
                                            )}
                                        </div>
                                    )}

                                    {trackingDetails.eta && (
                                        <div className="border-t pt-3 grid grid-cols-2 gap-4">
                                            {trackingDetails.eta.to_pickup !== undefined && (
                                                <div>
                                                    <div className="text-xs text-gray-500">Pickup ETA</div>
                                                    <div className="font-semibold text-gray-800 mt-0.5">{trackingDetails.eta.to_pickup} mins</div>
                                                </div>
                                            )}
                                            {trackingDetails.eta.to_delivery !== undefined && (
                                                <div>
                                                    <div className="text-xs text-gray-500">Delivery ETA</div>
                                                    <div className="font-semibold text-gray-800 mt-0.5">{trackingDetails.eta.to_delivery} mins</div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {trackingDetails.deliveryStaffDetails?.currentLocation?.lat && (
                                        <div className="border-t pt-3">
                                            <div className="text-xs text-gray-500 mb-1">Rider Location</div>
                                            <a 
                                                href={`https://www.google.com/maps/search/?api=1&query=${trackingDetails.deliveryStaffDetails.currentLocation.lat},${trackingDetails.deliveryStaffDetails.currentLocation.long}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg font-bold border border-emerald-200 inline-block hover:bg-emerald-100"
                                            >
                                                📍 View Live on Google Maps
                                            </a>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-sm text-gray-500 text-center py-4">No live tracking information available yet.</p>
                            )}
                        </div>

                        <div className="border-t pt-4 mt-4 flex justify-end gap-2 flex-shrink-0">
                            {trackingDetails && (
                                <button
                                    onClick={() => handleTrackDelivery(trackingOrder)}
                                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-all"
                                >
                                    Refresh Status
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setTrackingOrder(null);
                                    setTrackingDetails(null);
                                    setShowRawResponse(false);
                                }}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-xl transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={!!cancelConfirm}
                title="Cancel Order"
                message={`Are you sure you want to cancel Order #${cancelConfirm}? This will restore stock and initiate a refund if applicable.`}
                confirmText="Yes, Cancel Order"
                cancelText="Go Back"
                onConfirm={handleAdminCancel}
                onCancel={() => setCancelConfirm(null)}
            />
        </div>
    );
}
